"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { CourseCard } from "@/components/CourseCard";
import type { GetCoursesQueryResult } from "@/sanity.types";

interface CoursesGridProps {
  courses: GetCoursesQueryResult;
}

export default function CoursesGrid({ courses }: CoursesGridProps) {
  const [activeCategory, setActiveCategory] = useState<string>("All");

  // Derive unique categories from courses
  const categories = [
    "All",
    ...Array.from(
      new Set(courses.map((c) => c.category?.name).filter((name): name is string => !!name)),
    ),
  ];

  const filtered =
    activeCategory === "All" ? courses : courses.filter((c) => c.category?.name === activeCategory);

  return (
    <div>
      {/* Category filter pills */}
      <div className="flex flex-wrap gap-2 mb-10">
        {categories.map((cat) => {
          const isActive = activeCategory === cat;
          return (
            <motion.button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className={`relative px-5 py-2 rounded-full text-sm font-medium transition-colors border-2 ${
                isActive
                  ? "bg-[#0193cd] text-white border-[#0193cd]"
                  : "bg-background text-muted-foreground border-border hover:border-[#0193cd] hover:text-[#0193cd]"
              }`}
            >
              {cat}
            </motion.button>
          );
        })}
      </div>

      {/* Course count */}
      <motion.p
        key={filtered.length}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-sm mb-8 text-muted-foreground"
      >
        {filtered.length} {filtered.length === 1 ? "course" : "courses"} found
      </motion.p>

      {/* Grid with AnimatePresence for smooth filtering */}
      {filtered.length > 0 ? (
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {filtered.map((course) => (
              <motion.div
                key={course._id}
                layout
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <CourseCard course={course} href={`/courses/${course.slug}`} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-20 text-muted-foreground"
        >
          <p className="text-lg">No courses in this category yet.</p>
          <p className="text-sm mt-2">Check back soon.</p>
        </motion.div>
      )}
    </div>
  );
}
